import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useDataQuery, useDataMutation } from 'hooks';
import {
  Popover, List, Badge, ToggleButton
} from '@mui/material';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { faBell as faBellRegular } from '@fortawesome/free-regular-svg-icons';
import getText from 'context/language/getText';
import { urlMap, urlConstructor } from 'routes/urlMap';
import { getUser } from 'selectors';
import DataContainer from 'containers/data';
import InlineLoader from 'components/common/loaders/InlineLoader';
import {
  getNotifications,
  getUnreadNotificationsCount,
  markAllNotificationsRead,
  markNotificationRead,
} from './graphql';
import { NotificationRow, NotificationsHeader } from './components';
import { EmptyState } from '../emptyState';

// recentNotifications can return a notification with a null job
export const getNotificationUrl = (notification) => {
  const jobRequestId = notification.job?.jobRequest?.id;
  return jobRequestId ? urlConstructor(urlMap.JOB_REQUESTS, { id: jobRequestId }) : null;
};

export const NotificationsListContent = ({ notifications, unreadNotificationsCount, reexecuteQuery }) => {
  const history = useHistory();
  const user = useSelector(getUser);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);

  const handlePopoverClick = (event) => {
    setPopoverAnchorEl(event.currentTarget);
  };

  const [, executeMarkNotificationRead] = useDataMutation(markNotificationRead);
  const [, executeMarkAllNotificationsRead] = useDataMutation(
    markAllNotificationsRead,
  );

  const refreshData = () => {
    reexecuteQuery.notifications({ requestPolicy: 'network-only' });
    reexecuteQuery.unreadNotificationsCount({ requestPolicy: 'network-only' });
  };

  const onNotificationClick = async (notification) => {
    await executeMarkNotificationRead({ notificationId: notification.id });

    refreshData();

    const url = getNotificationUrl(notification);
    if (url) history.push(url);
  };

  const onMarkAllNotificationsReadClick = async () => {
    await executeMarkAllNotificationsRead();
    refreshData();
  };

  const onSettingsClick = () => {
    setPopoverAnchorEl(null);
    history.push(urlConstructor(urlMap.NOTIFICATION_PREFERENCES, { id: user.id }));
  };

  return (
    <>
      <ToggleButton
        size="small"
        sx={{ border: 'none', borderRadius: '50%' }}
        onClick={handlePopoverClick}
        selected={Boolean(popoverAnchorEl)}
        data-test-id="notifications__notification-toggle"
      >
        <Badge
          badgeContent={unreadNotificationsCount}
          slotProps={{
            badge: {
              sx: { color: 'notification.contrast', backgroundColor: 'notification.main' }
            }
          }}
        >
          <FontAwesomeIcon icon={faBell} color="black" size="xs" style={{ height: '16px' }} />
        </Badge>
      </ToggleButton>
      <Popover
        open={Boolean(popoverAnchorEl)}
        anchorEl={popoverAnchorEl}
        onClose={() => setPopoverAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List
          sx={{ width: '420px', maxHeight: '500px' }}
          subheader={(
            <NotificationsHeader
              onMarkAllNotificationsReadClick={onMarkAllNotificationsReadClick}
              onSettingsClick={onSettingsClick}
            />
          )}
          data-test-id="notifications__notification-list"
        >
          <div style={{ padding: '8px' }}>
            {
              (notifications && notifications.length > 0) ? notifications.map((notification) => (
                <NotificationRow
                  key={`notification-${notification.id}`}
                  message={notification.message}
                  createdAt={notification.createdAt}
                  read={notification.read}
                  onClick={() => onNotificationClick(notification)}
                />
              )) : (
                <EmptyState
                  title={getText('header.notifications.emptyState.title')}
                  description={getText('header.notifications.emptyState.description')}
                  icon={faBellRegular}
                />
              )
            }
          </div>
        </List>
      </Popover>
    </>
  );
};

NotificationsListContent.propTypes = {
  notifications: PropTypes.array.isRequired,
  unreadNotificationsCount: PropTypes.number.isRequired,
  reexecuteQuery: PropTypes.shape({
    notifications: PropTypes.func.isRequired,
    unreadNotificationsCount: PropTypes.func.isRequired
  }).isRequired
};

export const NotificationsList = () => (
  <DataContainer
    dataMap={{
      notifications: useDataQuery(getNotifications),
      unreadNotificationsCount: useDataQuery(getUnreadNotificationsCount)
    }}
    displayComponent={NotificationsListContent}
    loaderComponent={InlineLoader}
    errorComponent={() => null}
  />
);
